import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { makeBackendApi } from 'services/BackendApi';
import {
  IAnnouncementCreateRequest
} from 'services/BackendApi/types/AnnouncementResponse';
import { ESortOrder } from 'store/common/types';

/**
 * React Query hook for fetching announcements list with filters, sorting, and pagination
 */
export const useAnnouncementsList = (
  page: number,
  limit: number,
  sortBy: string,
  order: ESortOrder,
  filters: {
    title?: string;
    hotelUuid?: string;
  }
) => {
  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: ['announcements', 'list', page, limit, sortBy, order, filters.title, filters.hotelUuid],
    queryFn: async () => {
      const params: any = {
        page,
        limit,
        sortBy,
        sortOrder: order === ESortOrder.ASC ? 'asc' : 'desc',
      };

      if (filters.title) {
        params.title = filters.title;
      }
      if (filters.hotelUuid) {
        params.hotelUuid = filters.hotelUuid;
      }

      const response = await backendApi.getAnnouncements(params);
      return response.data;
    },
  });
};

/**
 * React Query mutation hook for creating a new announcement
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export const useCreateAnnouncement = () => {
  const backendApi = makeBackendApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (announcementData: IAnnouncementCreateRequest) => {
      const response = await backendApi.createAnnouncement(announcementData);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the announcements list to refetch after creating a new one
      queryClient.invalidateQueries({ queryKey: ['announcements', 'list'] });
    },
  });
};

/**
 * React Query hook for fetching a single announcement by UUID
 * Used for edit mode (future implementation)
 *
 * @param uuid - The announcement UUID to fetch (null disables the query)
 * @returns Query object with announcement data
 */
export const useAnnouncement = (uuid: string | null) => {
  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: ['announcements', 'detail', uuid],
    queryFn: async () => {
      if (!uuid) throw new Error('UUID is required');
      try {
        // Try to fetch from the detail endpoint first
        const response = await backendApi.getAnnouncement(uuid);
        return response.data.data;
      } catch (error) {
        // Fallback to fetching from list if detail endpoint doesn't exist
        const response = await backendApi.getAnnouncements({});
        const announcement = response.data.data.find(a => a.uuid === uuid);
        if (!announcement) throw new Error('Announcement not found');
        return announcement;
      }
    },
    enabled: !!uuid, // Only run query if UUID is provided
    staleTime: 0, // Always consider data stale, refetch on mount
  });
};

/**
 * React Query mutation hook for updating an existing announcement
 * Future implementation for edit mode
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export const useUpdateAnnouncement = () => {
  const backendApi = makeBackendApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ uuid, announcementData }: { uuid: string; announcementData: IAnnouncementCreateRequest }) => {
      const response = await backendApi.updateAnnouncement(uuid, announcementData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Update the cache with the fresh data
      queryClient.setQueryData(['announcements', 'detail', variables.uuid], data);
      // Invalidate the list to keep it in sync
      queryClient.invalidateQueries({ queryKey: ['announcements', 'list'] });
    },
  });
};

/**
 * React Query mutation hook for deleting an announcement
 *
 * @returns Mutation object with mutate/mutateAsync functions
 */
export const useDeleteAnnouncement = () => {
  const backendApi = makeBackendApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (uuid: string) => {
      const response = await backendApi.deleteAnnouncement(uuid);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate the announcements list to refetch after deletion
      queryClient.invalidateQueries({ queryKey: ['announcements', 'list'] });
    },
  });
};
